import { useKpisPerimeter } from '#composables/kpisPerimeter';
import { exportDataTable as exportDataTableQuery, type DataTableInputType } from '#domain/performance/kpi/kpi.query';
import {
  type DataTableDimensionsType,
  type DataTableKpiDisplayNameType,
  type DataTableViewType,
  type ExportOptionsType
} from '@carrefour-gcs/shared';
import { useMutation } from '@tanstack/vue-query';
import { unref, type Ref } from 'vue';

interface UseDataTableExportParams {
  dataTableView: Ref<DataTableViewType>;
  selectedDimensions: Ref<DataTableDimensionsType[] | null>;
  selectedKpis: Ref<DataTableKpiDisplayNameType[] | null>;
  benchmarkQuery?: Ref<DataTableInputType['benchmark']>;
}

export function useDataTableExport(params: UseDataTableExportParams) {
  const { dataTableView, selectedDimensions, selectedKpis, benchmarkQuery } = params;

  const {
    taxonomy: { taxonomy },
    withTax: { isWithTax },
    filters: { selectedFiltersKpiQuery },
    period: { selectedPeriod },
    storePerimeter: { storePerimeter },
    myDataIncluded: { isMyDataIncluded },
    detentionPeriod: { detentionPeriod }
  } = useKpisPerimeter();

  const {
    mutate: triggerExportMutation,
    isPending: isExportPending,
    isSuccess: isExportSucceeded,
    reset: resetExport
  } = useMutation({
    mutationFn: (exportOptions: ExportOptionsType) => {
      if (!selectedDimensions.value || !selectedKpis.value) {
        throw new Error('selectedDimensions and selectedKpis should be defined before export');
      }

      const eligibleDetentionViews = ['possessionZoomPage', 'possessionZoomPageWithTheoretical', 'performance'];

      return exportDataTableQuery({
        period: unref(selectedPeriod),
        dataTableView: unref(dataTableView),
        selectedDimensions: selectedDimensions.value as [DataTableDimensionsType, ...DataTableDimensionsType[]],
        selectedKpis: selectedKpis.value as [DataTableKpiDisplayNameType, ...DataTableKpiDisplayNameType[]],
        ...(eligibleDetentionViews.includes(dataTableView.value ?? '') && { detentionPeriod: detentionPeriod.value }),
        query: {
          filters: selectedFiltersKpiQuery.value,
          isWithTax: isWithTax.value,
          isMyDataIncluded: isMyDataIncluded.value
        },
        taxonomy: unref(taxonomy),
        storePerimeter: unref(storePerimeter),
        benchmark: benchmarkQuery?.value,
        exportOptions
      });
    }
  });

  function exportDataTable(exportOptions: ExportOptionsType) {
    triggerExportMutation(exportOptions);
  }

  return {
    isExportPending,
    isExportSucceeded,
    exportDataTable,
    resetExport
  };
}
